import { ApiError, ErrorCode } from '@approvalmax/types';
import { toast } from '@approvalmax/ui/src/components';
import { isEqual } from 'lodash';
import { backend } from 'modules/data';
import { clearCacheStorage } from 'modules/data-providers';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePrevious } from 'react-use';
import { CreateBeneficiaryRequest, useCreateBeneficiary, useGetBeneficiarySchema } from 'shared/data/webApp/v1';

import { useXeroAirwallexBatchPaymentFormContext } from '../../../../XeroAirwallexBatchPaymentForm.context';
import { tuneAirwallexFieldsAnswer } from '../AddBeneficiaryPopup/AddBeneficiaryPopup.helpers';
import {
    accountCurrencyKey,
    bankCountryCodeKey,
    entityTypeKey,
    localClearingSystemKey,
    paymentMethodKey,
} from './AddBeneficiaryPopupContent.constants';
import { messages } from './AddBeneficiaryPopupContent.messages';
import {
    UseBeneficiarySchemaProps,
    UseCreateBeneficiaryMutationProps,
    UseReloadSchemaIfLocalClearingFieldIsRequiredProps,
} from './AddBeneficiaryPopupContent.types';

export const useBeneficiarySchema = ({
    form,
    initialCurrency,
    closePopup,
    countryCode,
    activeStep,
    setActiveStep,
    fieldsSchemaTransfer,
    setFieldsSchemaTransfer,
}: UseBeneficiarySchemaProps) => {
    const prepopulateValue = useCallback(
        (fieldsSchema: backend.AirwallexBenefeciarySchemaField[]) => {
            const defaultFormValues = fieldsSchema.reduce<Record<string, string>>((acc, currentField) => {
                acc[currentField.path] = currentField.field.default;

                return acc;
            }, {});

            for (const key in defaultFormValues) {
                if (!form.getValues(key)) {
                    if (key === bankCountryCodeKey) {
                        switch (countryCode) {
                            case backend.OrganisationVersion.UK:
                                form.setValue(bankCountryCodeKey, backend.CountryCode.GB);
                                break;
                            case backend.OrganisationVersion.US:
                                form.setValue(bankCountryCodeKey, backend.CountryCode.US);
                                break;
                            case backend.OrganisationVersion.AU:
                                form.setValue(bankCountryCodeKey, backend.CountryCode.AU);
                                break;
                            case backend.OrganisationVersion.NZ:
                                form.setValue(bankCountryCodeKey, backend.CountryCode.NZ);
                                break;
                        }
                    } else {
                        form.setValue(key, defaultFormValues[key]);
                    }
                } else if (form.getFieldState(key).isDirty) {
                    const field = fieldsSchema.find((field) => field.path === key);

                    if (!field || !field.field.options) continue;

                    const selectedOption = field.field.options.find((option) => option.value === form.getValues(key));

                    if (!selectedOption) {
                        form.setValue(key, defaultFormValues[key]);
                    }
                } else {
                    form.setValue(key, defaultFormValues[key]);
                }
            }

            if (!form.getValues(accountCurrencyKey)) {
                form.setValue(accountCurrencyKey, initialCurrency || '');
            }
        },
        [countryCode, form, initialCurrency]
    );

    const checkInitialCurrency = useCallback(
        (fieldsSchema: backend.AirwallexBenefeciarySchemaField[]) => {
            const fieldCurrency = fieldsSchema.find((field) => field.path === accountCurrencyKey);

            if (fieldCurrency) {
                const isCurrencySupported = Boolean(
                    fieldCurrency.field.options?.find((option) => option.value === initialCurrency)
                );

                if (!isCurrencySupported) {
                    toast.error(messages.notSupportedCurrency({ currency: initialCurrency }));
                    closePopup();
                }
            }
        },
        [closePopup, initialCurrency]
    );

    const {
        refetch: getBeneficiarySchema,
        data,
        isFetching: isFetchingBeneficiarySchema,
    } = useGetBeneficiarySchema(
        {
            query: fieldsSchemaTransfer,
        },
        {
            cacheTime: 0,
            onSuccess: (data) => {
                const fieldsSchema = tuneAirwallexFieldsAnswer(data);

                prepopulateValue(fieldsSchema);

                if (activeStep === 1) {
                    checkInitialCurrency(fieldsSchema);
                }

                if (activeStep === 2) {
                    const paymentMethodField = fieldsSchema.find((field) => field.path === paymentMethodKey);
                    const paymentMethodOptions = paymentMethodField?.field.options;

                    if (paymentMethodOptions && !paymentMethodOptions.length) {
                        toast.error(messages.notSupported);

                        setFieldsSchemaTransfer({
                            ...fieldsSchemaTransfer,
                            bankCountryCode: undefined,
                            accountCurrency: undefined,
                        });

                        setActiveStep(1);
                    }
                }
            },
        }
    );

    const beneficiarySchema = useMemo(() => {
        return data ? tuneAirwallexFieldsAnswer(data) : undefined;
    }, [data]);

    return {
        getBeneficiarySchema,
        beneficiarySchema,
        isFetchingBeneficiarySchema,
    };
};

export const useCreateBeneficiaryMutation = ({
    setIsOpenSuccessPopup,
    setIsOpen2FA,
    setErrorMessages,
    initialBillId,
    beneficiarySchema,
}: UseCreateBeneficiaryMutationProps) => {
    const dispatch = useDispatch();
    const { changeBeneficiary } = useXeroAirwallexBatchPaymentFormContext();

    const onSuccess = useCallback(
        (response: CreateBeneficiaryRequest['response']) => {
            setIsOpenSuccessPopup(true);
            setIsOpen2FA(false);
            dispatch(clearCacheStorage((storageId: string) => storageId.startsWith('requestOptions')));

            if (initialBillId && response.beneficiary) {
                changeBeneficiary(initialBillId, {
                    ...response.beneficiary,
                    bankDetails: {
                        ...response.beneficiary.bankDetails,
                        accountingRoutingType1:
                            response.beneficiary.bankDetails.accountingRoutingType1 ||
                            response.beneficiary.bankDetails.accountRoutingType1 ||
                            '',
                        accountingRoutingType2:
                            response.beneficiary.bankDetails.accountingRoutingType2 ||
                            response.beneficiary.bankDetails.accountRoutingType2 ||
                            '',
                        accountingRoutingValue1:
                            response.beneficiary.bankDetails.accountingRoutingValue1 ||
                            response.beneficiary.bankDetails.accountRoutingValue1 ||
                            '',
                        accountingRoutingValue2:
                            response.beneficiary.bankDetails.accountingRoutingValue2 ||
                            response.beneficiary.bankDetails.accountRoutingValue2 ||
                            '',
                    },
                    text: response.beneficiary.name,
                    paymentFee: null,
                    paymentMethod: response.beneficiary.paymentMethod
                        ? { id: response.beneficiary.paymentMethod, text: response.beneficiary.paymentMethod }
                        : null,
                    id: response.beneficiary.airwallexBeneficiaryId,
                });
            }
        },
        [changeBeneficiary, dispatch, initialBillId, setIsOpen2FA, setIsOpenSuccessPopup]
    );

    const onError = useCallback(
        (error: ApiError) => {
            if (error.code === ErrorCode.E4002_WRONG_CONFIRMATION_CODE) {
                setErrorMessages([error.title || messages.unknownError]);
            } else if (error.code === ErrorCode.E7711_BENEFICIARY_CREATION_VALIDATION_ERROR && error.errors?.length) {
                const errorMessages = error.errors.map((error) => `${error.path}: ${error.message}`);

                setErrorMessages(errorMessages);
            } else {
                let errorText: string = messages.unknownError;
                let parameterName = '';

                if (error.parameterName && beneficiarySchema) {
                    const field = beneficiarySchema.find((field) => field.path === error.parameterName);

                    if (field) {
                        parameterName = field.field.label;
                    }
                }

                if (error.detail && parameterName) {
                    errorText = `${error.detail} (${parameterName})`;
                } else if (error.detail) {
                    errorText = error.detail;
                }

                setErrorMessages([errorText]);
            }

            setIsOpen2FA(false);
        },
        [beneficiarySchema, setErrorMessages, setIsOpen2FA]
    );

    const onMutate = useCallback(() => {
        setIsOpen2FA(false);
    }, [setIsOpen2FA]);

    return useCreateBeneficiary({
        onSuccess,
        onError,
        onMutate,
    });
};

export const useReloadSchemaIfLocalClearingFieldIsRequired = ({
    activeStep,
    form,
    beneficiarySchema,
    fieldsSchemaTransfer,
    companyId,
    setFieldsSchemaTransfer,
}: UseReloadSchemaIfLocalClearingFieldIsRequiredProps) => {
    const paymentMethod = form.watch(paymentMethodKey);
    const prePaymentMethod = usePrevious(paymentMethod);

    if (activeStep !== 2) return;

    const onlyPaymentMethodChanged = paymentMethod !== prePaymentMethod;

    const missLocalClearingField =
        beneficiarySchema &&
        form.getValues(paymentMethodKey) === 'LOCAL' &&
        !beneficiarySchema.find((field) => field.path === localClearingSystemKey);

    if (onlyPaymentMethodChanged || missLocalClearingField) {
        const newFieldsSchemaTransfer = {
            companyId,
            accountCurrency: form.getValues(accountCurrencyKey),
            bankCountryCode: form.getValues(bankCountryCodeKey),
            entityType: form.getValues(entityTypeKey),
            paymentMethod: paymentMethod,
        };

        if (!isEqual(newFieldsSchemaTransfer, fieldsSchemaTransfer)) {
            setFieldsSchemaTransfer(newFieldsSchemaTransfer);
        }
    }
};

export const useNestedPopups = () => {
    const [isOpen2FA, setIsOpen2FA] = useState(false);
    const [isOpenSuccessPopup, setIsOpenSuccessPopup] = useState(false);

    const close2FAPopup = useCallback(() => setIsOpen2FA(false), []);

    return {
        isOpen2FA,
        setIsOpen2FA,
        close2FAPopup,
        isOpenSuccessPopup,
        setIsOpenSuccessPopup,
    };
};
